import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import {
  Container,
  Typography,
  Paper,
  Box,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Switch,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  alpha,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Person,
  PhotoLibrary,
  VideoLibrary,
  PhoneAndroid,
  Computer,
  ContentCopy,
  CheckCircle,
  FilterBAndW as MonoIcon,
  AccountBalanceWallet as PayOsWalletIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LockOutlined as LockOutlinedIcon,
  Palette as PaletteIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as api from '../../services/api';
import { currentAppOrigin, API_URL, SOCKET_URL } from '../../config/api';
import AdminMPhotoPro from './AdminMPhotoPro';
import AdminVideos from './Videos';
import AdminMPhotoMono from './MPhotoMono';
import AdminMPhotoLite from './MPhotoLite';
import UserSePay from './UserSePay';
import AppAccentColorApps from '../shared/AppAccentColorApps';
import MachineDeviceMgmtTabs from '../shared/MachineDeviceMgmtTabs';
import MachineRenameControl from '../shared/MachineRenameControl';
import TabPanelEnter from '../shared/TabPanelEnter';
import { userCardEnterSx } from '../shared/UserPageShell';
import { useI18n } from '../../i18n/useI18n';
import UserStaffManager from '../shared/UserStaffManager';

const appProductLabelColor = (product?: string | null) => {
  const p = String(product || '').trim().toLowerCase();
  if (p === 'lite') return '#0284c7';
  if (p === 'mono') return '#4b5563';
  if (p === 'pro') return '#c2410c';
  return undefined;
};

const profileFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: 'common.white',
    '& fieldset': {
      borderColor: 'divider',
    },
    '&:hover fieldset': {
      borderColor: 'primary.main',
    },
    '&.Mui-focused fieldset': {
      borderWidth: 1.5,
      borderColor: 'primary.main',
    },
  },
};

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { admin: a, pages: t, formatMessage, lang } = useI18n();
  const [user, setUser] = useState<api.AdminUserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingMobileUpload, setUpdatingMobileUpload] = useState(false);
  const [mobileUploadEnabled, setMobileUploadEnabled] = useState(false);
  const [editPageEnabled, setEditPageEnabled] = useState(false);
  const [multiCaptureEnabled, setMultiCaptureEnabled] = useState(false);
  const [updatingEditPage, setUpdatingEditPage] = useState(false);
  const [updatingMultiCapture, setUpdatingMultiCapture] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [machines, setMachines] = useState<api.MachineInfo[]>([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [selectedMachineCode, setSelectedMachineCode] = useState<string | null>(null);
  const [machineDetail, setMachineDetail] = useState<api.AdminMachineRandomCodeResponse['machine'] | null>(null);
  const [machineDetailLoading, setMachineDetailLoading] = useState(false);
  const [copiedTarget, setCopiedTarget] = useState<'random' | 'link' | null>(null);
  const rightMachinePanelRef = useRef<HTMLDivElement | null>(null);
  const [rightMachinePanelHeight, setRightMachinePanelHeight] = useState(0);
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [deviceAccess, setDeviceAccess] = useState({ pro: true, lite: false, mono: false });
  const [updatingDeviceAccess, setUpdatingDeviceAccess] = useState(false);
  const [deviceAccessOpen, setDeviceAccessOpen] = useState(false);
  const [galleryUploadMethod, setGalleryUploadMethod] = useState<'server' | 'signedUrl'>('server');
  const [updatingUploadMethod, setUpdatingUploadMethod] = useState(false);
  const [loginDurationDays, setLoginDurationDays] = useState<Record<api.SessionChannel, number | null>>({
    pro: null,
    lite: null,
    mono: null,
  });
  const [sessionDraftDays, setSessionDraftDays] = useState<Record<api.SessionChannel, string>>({
    pro: '',
    lite: '',
    mono: '',
  });
  const [sessionUnlimited, setSessionUnlimited] = useState<Record<api.SessionChannel, boolean>>({
    pro: true,
    lite: true,
    mono: true,
  });
  const [updatingSessionChannel, setUpdatingSessionChannel] = useState<api.SessionChannel | null>(null);
  const [collectionRetentionDays, setCollectionRetentionDays] = useState<
    Record<api.SessionChannel, number>
  >({
    pro: api.COLLECTION_RETENTION_DEFAULT_DAYS,
    lite: api.COLLECTION_RETENTION_DEFAULT_DAYS,
    mono: api.COLLECTION_RETENTION_DEFAULT_DAYS,
  });
  const [collectionDraftDays, setCollectionDraftDays] = useState<Record<api.SessionChannel, string>>({
    pro: String(api.COLLECTION_RETENTION_DEFAULT_DAYS),
    lite: String(api.COLLECTION_RETENTION_DEFAULT_DAYS),
    mono: String(api.COLLECTION_RETENTION_DEFAULT_DAYS),
  });
  const [updatingCollectionChannel, setUpdatingCollectionChannel] = useState<api.SessionChannel | null>(
    null,
  );
  const [forcingLogoutChannel, setForcingLogoutChannel] = useState<api.SessionChannel | null>(null);
  const [forcingLogoutMachine, setForcingLogoutMachine] = useState<string | null>(null);
  const [logoutConfirm, setLogoutConfirm] = useState<
    | { type: 'channel'; channel: api.SessionChannel }
    | { type: 'machine'; machineCode: string; label: string }
    | null
  >(null);
  const machineSocketRef = useRef<Socket | null>(null);

  // Xác định tab hiện tại dựa trên URL
  const getCurrentTab = () => {
    const path = location.pathname;
    // Lite user-frame assets: /users/:id/m-photo-lite/custom-frames/:frameId
    if (path.includes('/m-photo-lite')) return 'm-photo-lite';
    // Collections nằm trong tab Pro — URL cũ /collections vẫn map sang Pro
    if (path.includes('/photos') || path.includes('/m-photo-pro') || path.includes('/collections')) {
      return 'm-photo-pro';
    }
    if (path.includes('/videos')) return 'videos';
    if (path.includes('/icons')) return 'm-photo-lite';
    if (path.includes('/media')) return 'media';
    if (path.includes('/mobile-upload')) return 'mobile-upload';
    if (path.includes('/m-photo-mono')) return 'm-photo-mono';
    if (path.includes('/sepay')) return 'sepay';
    if (path.includes('/staff')) return 'staff';
    return 'profile';
  };

  const [currentTab, setCurrentTab] = useState(getCurrentTab());
  /** Giữ Pro/Lite/Mono trong DOM sau lần mở đầu — đổi tab không load lại */
  const [mountedProductTabs, setMountedProductTabs] = useState<Set<string>>(() => {
    const t = getCurrentTab();
    return ['m-photo-pro', 'm-photo-lite', 'm-photo-mono'].includes(t) ? new Set([t]) : new Set();
  });

  useEffect(() => {
    setCurrentTab(getCurrentTab());
  }, [location.pathname]);

  useEffect(() => {
    if (!['m-photo-pro', 'm-photo-lite', 'm-photo-mono'].includes(currentTab)) return;
    setMountedProductTabs((prev) => {
      if (prev.has(currentTab)) return prev;
      const next = new Set(prev);
      next.add(currentTab);
      return next;
    });
  }, [currentTab]);

  useEffect(() => {
    if (!userId) return;
    const base = `/users/${userId}`;
    if (!location.pathname.startsWith(`${base}/`) && location.pathname !== base) return;
    if (location.pathname.includes('/icons')) {
      navigate(`${base}/m-photo-lite?tab=icons`, { replace: true });
    } else if (location.pathname.includes('/collections')) {
      navigate(`${base}/m-photo-pro?proTab=collections`, { replace: true });
    } else if (/\/custom-frames\/?$/.test(location.pathname)) {
      navigate(`${base}/m-photo-pro?proTab=custom-frames`, { replace: true });
    } else if (location.pathname.includes('/device')) {
      navigate(base, { replace: true });
    }
  }, [userId, location.pathname, navigate]);

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [userId]);

  useEffect(() => {
    if (currentTab === 'mobile-upload' && userId && mobileUploadEnabled) {
      fetchAdminUserMachines();
    }
  }, [currentTab, userId, mobileUploadEnabled]);

  useEffect(() => {
    if (currentTab !== 'mobile-upload') return;
    const element = rightMachinePanelRef.current;
    if (!element) return;

    const updateHeight = () => {
      setRightMachinePanelHeight(element.offsetHeight || 0);
    };

    updateHeight();
    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(element);

    return () => observer.disconnect();
  }, [currentTab, machineDetailLoading, machineDetail, machines.length, mobileUploadEnabled]);

  const machineCodesKey = machines.map((m) => m.machineCode.toUpperCase()).join(',');

  useEffect(() => {
    if (currentTab !== 'mobile-upload' || !machineCodesKey) {
      if (machineSocketRef.current) {
        machineSocketRef.current.disconnect();
        machineSocketRef.current = null;
      }
      return;
    }

    const codes = machineCodesKey.split(',').filter(Boolean);
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      path: '/socket.io',
    });
    machineSocketRef.current = socket;

    const joinRooms = () => {
      codes.forEach((code) => {
        socket.emit('join-machine-room', code);
      });
    };
    socket.on('connect', joinRooms);

    socket.on('app-status-update', (data: {
      machineCode: string;
      isAppRunning: boolean;
      appPlatform?: 'windows' | 'android' | null;
      appProduct?: 'lite' | 'mono' | 'pro' | null;
    }) => {
      if (!data?.machineCode) return;
      setMachines((prev) =>
        prev.map((m) =>
          m.machineCode.toUpperCase() === data.machineCode.toUpperCase()
            ? {
                ...m,
                isAppRunning: data.isAppRunning,
                appPlatform: data.isAppRunning
                  ? (data.appPlatform ?? m.appPlatform)
                  : m.appPlatform,
                appProduct: data.isAppRunning
                  ? (data.appProduct ?? m.appProduct)
                  : m.appProduct,
              }
            : m
        )
      );
      setMachineDetail((prev) => {
        if (prev && prev.machineCode.toUpperCase() === data.machineCode.toUpperCase()) {
          return {
            ...prev,
            isAppRunning: data.isAppRunning,
            appPlatform: data.isAppRunning
              ? (data.appPlatform ?? prev.appPlatform)
              : prev.appPlatform,
            appProduct: data.isAppRunning
              ? (data.appProduct ?? prev.appProduct)
              : prev.appProduct,
          };
        }
        return prev;
      });
    });

    return () => {
      codes.forEach((code) => {
        socket.emit('leave-machine-room', code);
      });
      socket.disconnect();
      machineSocketRef.current = null;
    };
  }, [currentTab, machineCodesKey]);

  const fetchUser = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const userData = await api.getUserDetails(userId);
      setUser(userData);
      setMobileUploadEnabled(userData.mobileUploadEnabled || false);
      setEditPageEnabled(userData.editPageEnabled === true);
      setMultiCaptureEnabled(userData.multiCaptureEnabled === true);
      setDeviceAccess({
        pro: userData.deviceAccess?.pro ?? true,
        lite: userData.deviceAccess?.lite ?? false,
        mono: userData.deviceAccess?.mono ?? false,
      });
      setGalleryUploadMethod(userData.galleryUploadMethod === 'signedUrl' ? 'signedUrl' : 'server');
      try {
        const policy = await api.getAdminUserSessionPolicy(userId);
        setLoginDurationDays(policy.loginDurationDays);
        (['pro', 'lite', 'mono'] as api.SessionChannel[]).forEach((ch) => {
          const days = policy.loginDurationDays[ch];
          setSessionUnlimited((prev) => ({ ...prev, [ch]: days == null }));
          setSessionDraftDays((prev) => ({
            ...prev,
            [ch]: days == null ? '7' : String(days),
          }));
        });
      } catch {
        /* session policy optional on older backend */
      }
      try {
        const retention = await api.getAdminUserCollectionRetention(userId);
        setCollectionRetentionDays(retention.collectionRetentionDays);
        (['pro', 'lite', 'mono'] as api.SessionChannel[]).forEach((ch) => {
          const days =
            retention.collectionRetentionDays[ch] ?? api.COLLECTION_RETENTION_DEFAULT_DAYS;
          setCollectionDraftDays((prev) => ({ ...prev, [ch]: String(days) }));
        });
      } catch {
        /* collection retention optional on older backend */
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error(a.userDetail.toastLoadUserFail);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
    if (newValue === 'profile') {
      navigate(`/users/${userId}`);
    } else if (newValue === 'mobile-upload') {
      navigate(`/users/${userId}/mobile-upload`);
    } else {
      navigate(`/users/${userId}/${newValue}`);
    }
  };

  const handleToggleMobileUpload = async () => {
    if (!userId) return;
    
    try {
      setUpdatingMobileUpload(true);
      await api.updateMobileUploadStatus(userId, !mobileUploadEnabled);
      
      const newStatus = !mobileUploadEnabled;
      setMobileUploadEnabled(newStatus);
      
      if (user) {
        setUser({ ...user, mobileUploadEnabled: newStatus });
      }
      
      toast.success(a.userDetail.toastDeviceUpdated);
    } catch (error) {
      console.error('Error updating mobile upload status:', error);
      toast.error(a.userDetail.toastDeviceUpdateFail);
    } finally {
      setUpdatingMobileUpload(false);
    }
  };

  const handleToggleEditPage = async () => {
    if (!userId) return;
    try {
      setUpdatingEditPage(true);
      const next = !editPageEnabled;
      const res = await api.updateMobileUploadStatus(userId, { editPageEnabled: next });
      const enabled = res.editPageEnabled === true;
      setEditPageEnabled(enabled);
      if (user) setUser({ ...user, editPageEnabled: enabled });
      toast.success(enabled ? a.userDetail.toastEditOn : a.userDetail.toastEditOff);
    } catch (error) {
      console.error('Error updating edit page status:', error);
      toast.error(a.userDetail.toastEditFail);
    } finally {
      setUpdatingEditPage(false);
    }
  };

  const handleToggleMultiCapture = async () => {
    if (!userId) return;
    try {
      setUpdatingMultiCapture(true);
      const next = !multiCaptureEnabled;
      const res = await api.updateMobileUploadStatus(userId, { multiCaptureEnabled: next });
      const enabled = res.multiCaptureEnabled === true;
      setMultiCaptureEnabled(enabled);
      if (user) setUser({ ...user, multiCaptureEnabled: enabled });
      toast.success(enabled ? a.userDetail.toastMergeOn : a.userDetail.toastMergeOff);
    } catch (error) {
      console.error('Error updating multi capture status:', error);
      toast.error(a.userDetail.toastMergeFail);
    } finally {
      setUpdatingMultiCapture(false);
    }
  };

  const handleUpdateDeviceAccess = async (key: 'pro' | 'lite' | 'mono', enabled: boolean) => {
    if (!userId) return;
    try {
      setUpdatingDeviceAccess(true);
      const payload = { ...deviceAccess, [key]: enabled };
      const response = await api.updateAdminUserDeviceAccess(userId, payload);
      setDeviceAccess(response.deviceAccess);
      toast.success(a.userDetail.toastAccessUpdated);
    } catch (error: any) {
      console.error('Error updating device access:', error);
      toast.error(error?.response?.data?.error || a.userDetail.toastAccessFail);
    } finally {
      setUpdatingDeviceAccess(false);
    }
  };

  const handleUpdateGalleryUploadMethod = async (method: 'server' | 'signedUrl') => {
    if (!userId || method === galleryUploadMethod) return;
    try {
      setUpdatingUploadMethod(true);
      const response = await api.updateAdminUserGalleryUploadMethod(userId, method);
      setGalleryUploadMethod(response.galleryUploadMethod === 'signedUrl' ? 'signedUrl' : 'server');
      if (user) setUser({ ...user, galleryUploadMethod: response.galleryUploadMethod });
      if (response.warning) toast.warn(response.warning);
      else toast.success(a.userDetail.toastUploadMethodUpdated);
    } catch (error: any) {
      console.error('Error updating gallery upload method:', error);
      toast.error(error?.response?.data?.error || a.userDetail.toastUploadMethodFail);
    } finally {
      setUpdatingUploadMethod(false);
    }
  };

  const handleSaveSessionPolicy = async (channel: api.SessionChannel) => {
    if (!userId) return;
    const days = sessionUnlimited[channel]
      ? null
      : Math.max(1, Math.floor(Number(sessionDraftDays[channel]) || 1));
    try {
      setUpdatingSessionChannel(channel);
      const res = await api.updateAdminUserSessionPolicy(userId, { [channel]: days });
      setLoginDurationDays(res.loginDurationDays);
      toast.success('Đã cập nhật thời gian đăng nhập');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không cập nhật được thời gian đăng nhập');
    } finally {
      setUpdatingSessionChannel(null);
    }
  };

  const clampCollectionRetentionDays = (raw: string) => {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return api.COLLECTION_RETENTION_DEFAULT_DAYS;
    return Math.min(
      api.COLLECTION_RETENTION_MAX_DAYS,
      Math.max(api.COLLECTION_RETENTION_MIN_DAYS, n),
    );
  };

  const handleSaveCollectionRetention = async (channel: api.SessionChannel) => {
    if (!userId) return;
    const days = clampCollectionRetentionDays(collectionDraftDays[channel]);
    try {
      setUpdatingCollectionChannel(channel);
      const res = await api.updateAdminUserCollectionRetention(userId, { [channel]: days });
      setCollectionRetentionDays(res.collectionRetentionDays);
      setCollectionDraftDays((prev) => ({
        ...prev,
        [channel]: String(res.collectionRetentionDays[channel] ?? days),
      }));
      toast.success('Đã cập nhật thời gian hết hạn collection');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không cập nhật được thời gian hết hạn collection');
    } finally {
      setUpdatingCollectionChannel(null);
    }
  };

  const handleForceLogoutChannel = (channel: api.SessionChannel) => {
    if (!userId) return;
    setLogoutConfirm({ type: 'channel', channel });
  };

  const handleForceLogoutMachine = (machineCode: string, machineLabel?: string) => {
    if (!userId || !machineCode) return;
    const label = (machineLabel || machineCode).trim() || machineCode;
    setLogoutConfirm({ type: 'machine', machineCode, label });
  };

  const closeLogoutConfirm = () => {
    if (forcingLogoutChannel || forcingLogoutMachine) return;
    setLogoutConfirm(null);
  };

  const confirmForceLogout = async () => {
    if (!userId || !logoutConfirm) return;
    if (logoutConfirm.type === 'channel') {
      const channel = logoutConfirm.channel;
      try {
        setForcingLogoutChannel(channel);
        const res = await api.forceLogoutAdminUserChannel(userId, channel);
        setLoginDurationDays((prev) => ({ ...prev, ...res.loginDurationDays }));
        toast.success(`Đã gửi lệnh đăng xuất tất cả máy ${channel.toUpperCase()}`);
        setLogoutConfirm(null);
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Không gửi được lệnh đăng xuất');
      } finally {
        setForcingLogoutChannel(null);
      }
      return;
    }

    const { machineCode } = logoutConfirm;
    try {
      setForcingLogoutMachine(machineCode);
      await api.forceLogoutAdminUserMachine(userId, machineCode);
      toast.success(`Đã gửi lệnh đăng xuất máy ${machineCode}`);
      setLogoutConfirm(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không gửi được lệnh đăng xuất máy');
    } finally {
      setForcingLogoutMachine(null);
    }
  };

  const handleOpenPasswordDialog = () => {
    if (!newPassword) {
      toast.error(a.userDetail.toastNeedNewPassword);
      return;
    }

    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    if (updatingPassword) return;
    setPasswordDialogOpen(false);
    setAdminPassword('');
  };

  const handleAvatarAreaClick = () => {
    if (uploadingAvatar) return;
    avatarFileInputRef.current?.click();
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !userId) return;
    if (!file.type.startsWith('image/')) {
      toast.error(a.userDetail.toastAvatarType);
      return;
    }
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', file);
      let res: { avatarUrl: string };
      try {
        res = await api.adminUploadUserAvatar(userId, formData);
      } catch (error: any) {
        // Backend cũ chưa có /admin/users/:userId/avatar => fallback sang PUT /admin/users/:userId
        if (error?.response?.status === 404) {
          res = await api.adminUploadUserAvatarViaUpdateUser(userId, formData);
        } else {
          throw error;
        }
      }

      setUser((prev) => (prev ? { ...prev, avatarUrl: res.avatarUrl || prev.avatarUrl } : null));
      toast.success(a.userDetail.toastAvatarOk);
    } catch (error: any) {
      console.error('Error uploading user avatar:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        a.userDetail.toastAvatarFail;
      toast.error(message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleConfirmChangePassword = async () => {
    if (!userId) return;

    if (!adminPassword) {
      toast.error(a.userDetail.toastNeedAdminPassword);
      return;
    }

    try {
      setUpdatingPassword(true);
      await api.adminChangeUserPassword(userId, adminPassword, newPassword);
      toast.success(a.userDetail.toastPasswordUpdated);
      setNewPassword('');
      setAdminPassword('');
      setPasswordDialogOpen(false);
    } catch (error: any) {
      console.error('Error changing user password:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        a.userDetail.toastPasswordFail;
      toast.error(message);
    } finally {
      setUpdatingPassword(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(lang === 'ko' ? 'ko-KR' : lang === 'zh' ? 'zh-CN' : lang === 'en' ? 'en-US' : 'vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const roleLabel = (role?: string | null) => {
    const r = String(role || '').toUpperCase();
    if (r === 'ADMIN') return a.userDetail.roleAdmin;
    if (r === 'STAFF') return a.userDetail.roleStaff;
    if (r === 'USER') return a.userDetail.roleUser;
    return role || '';
  };
  const statusLabel = (status?: string | null) =>
    status === 'ACTIVE' ? a.userDetail.statusActive : a.userDetail.statusInactive;

  const fetchAdminUserMachines = async () => {
    if (!userId) return;
    try {
      setMachinesLoading(true);
      const response = await api.getAdminUserMachines(userId);
      const list = response.machines || [];
      setMachines(list);

      if (list.length > 0) {
        const preferred = new URLSearchParams(location.search).get('machine')?.trim();
        const matched = preferred
          ? list.find((m) => m.machineCode.toUpperCase() === preferred.toUpperCase())
          : undefined;
        const codeToSelect = matched?.machineCode || list[0].machineCode;
        setSelectedMachineCode(codeToSelect);
        await fetchAdminMachineDetail(codeToSelect);
        if (preferred && !matched) {
          toast.warning(`Không tìm thấy máy ${preferred} trên tài khoản này`);
        }
      } else {
        setSelectedMachineCode(null);
        setMachineDetail(null);
      }
    } catch (error) {
      console.error('Error loading admin user machines:', error);
      toast.error(a.userDetail.toastMachinesFail);
      setMachines([]);
      setSelectedMachineCode(null);
      setMachineDetail(null);
    } finally {
      setMachinesLoading(false);
    }
  };

  const fetchAdminMachineDetail = async (machineCode: string) => {
    if (!userId) return;
    try {
      setMachineDetailLoading(true);
      const response = await api.getAdminUserMachineRandomCode(userId, machineCode);
      setMachineDetail(response.machine);
    } catch (error) {
      console.error('Error loading admin machine detail:', error);
      toast.error(a.userDetail.toastMachineDetailFail);
      setMachineDetail(null);
    } finally {
      setMachineDetailLoading(false);
    }
  };

  const handleSelectMachine = async (machineCode: string) => {
    setSelectedMachineCode(machineCode);
    navigate(`/users/${userId}/mobile-upload?machine=${encodeURIComponent(machineCode)}`, {
      replace: true,
    });
    await fetchAdminMachineDetail(machineCode);
  };

  const handleCopyText = async (text: string, target: 'random' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTarget(target);
      toast.success(target === 'random' ? a.userDetail.toastCopiedCode : a.userDetail.toastCopiedLink);
      setTimeout(() => setCopiedTarget(null), 2000);
    } catch (error) {
      toast.error(a.userDetail.toastCopyFail);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" color="error">
          {a.userDetail.userNotFound}
        </Typography>
      </Container>
    );
  }

  const tabs = [
    { label: a.userDetail.tabs.profile, icon: <Person />, value: 'profile' },
    { label: isMobile ? a.userDetail.tabs.pro : a.userDetail.tabs.proFull, icon: <PhotoLibrary />, value: 'm-photo-pro' },
    { label: isMobile ? a.userDetail.tabs.lite : a.userDetail.tabs.liteFull, icon: <PhoneAndroid />, value: 'm-photo-lite' },
    { label: isMobile ? a.userDetail.tabs.mono : a.userDetail.tabs.monoFull, icon: <MonoIcon />, value: 'm-photo-mono' },
    { label: a.userDetail.tabs.media, icon: <VideoLibrary />, value: 'media' },
    { label: isMobile ? a.userDetail.tabs.device : a.userDetail.tabs.deviceFull, icon: <PhoneAndroid />, value: 'mobile-upload' },
    { label: a.userDetail.tabs.sepay, icon: <PayOsWalletIcon />, value: 'sepay' },
    { label: a.userDetail.tabs.staff, icon: <GroupsIcon />, value: 'staff' },
  ];

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1.5, md: 3 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: { xs: '100%', lg: undefined },
        boxSizing: 'border-box',
        overflow: 'visible',
      }}
    >
      {/* Profile + Device Access — cuộn bình thường */}
      <Paper
        elevation={0}
        sx={{
          mb: { xs: 1.25, md: 3 },
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (t) => `0 12px 32px ${alpha(t.palette.primary.main, 0.1)}`,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 1.25, md: 1.75 },
            color: 'common.white',
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 48%, ${t.palette.secondary.main} 100%)`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Tooltip title="Click để chọn và đổi ảnh đại diện cho user (logo tài khoản)" arrow>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <input
                  ref={avatarFileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleAvatarFileChange}
                />
                <Avatar
                  src={user.avatarUrl || undefined}
                  alt={user.username}
                  onClick={handleAvatarAreaClick}
                  sx={{
                    width: 42,
                    height: 42,
                    bgcolor: alpha('#fff', 0.22),
                    fontSize: '1.05rem',
                    cursor: uploadingAvatar ? 'wait' : 'pointer',
                    border: '2px solid',
                    borderColor: alpha('#fff', 0.55),
                    opacity: uploadingAvatar ? 0.65 : 1,
                  }}
                >
                  {!user.avatarUrl ? user.username.charAt(0).toUpperCase() : null}
                </Avatar>
                {uploadingAvatar && (
                  <CircularProgress
                    size={18}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-9px',
                      marginLeft: '-9px',
                      color: 'common.white',
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                    lineHeight: 1.2,
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.username}
                </Typography>
                <Chip
                  label={roleLabel(user.role)}
                  size="small"
                  sx={{
                    height: 22,
                    flexShrink: 0,
                    fontWeight: 700,
                    bgcolor: alpha('#fff', 0.22),
                    color: 'common.white',
                    '& .MuiChip-label': { px: 0.9 },
                  }}
                />
                <Chip
                  label={statusLabel(user.status)}
                  size="small"
                  sx={{
                    height: 22,
                    flexShrink: 0,
                    fontWeight: 700,
                    bgcolor: user.status === 'ACTIVE' ? alpha('#22c55e', 0.95) : alpha('#ef4444', 0.95),
                    color: 'common.white',
                    '& .MuiChip-label': { px: 0.9 },
                  }}
                />
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  mt: 0.15,
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.email}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.25, md: 2 }, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box
            onClick={() => setDeviceAccessOpen((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDeviceAccessOpen((v) => !v);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
              borderRadius: 2,
              px: 0.25,
              py: 0.25,
              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="subtitle2" fontWeight={800}>
                  {a.userDetail.deviceAccess}
                </Typography>
                {!deviceAccessOpen && (
                  <Stack direction="row" spacing={0.4}>
                    {(['pro', 'lite', 'mono'] as const).map((key) => (
                      <Chip
                        key={key}
                        size="small"
                        label={key.toUpperCase()}
                        sx={{
                          height: 20,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          bgcolor: deviceAccess[key]
                            ? alpha(theme.palette.success.main, 0.14)
                            : alpha(theme.palette.grey[500], 0.12),
                          color: deviceAccess[key] ? 'success.dark' : 'text.secondary',
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: deviceAccessOpen ? 'block' : 'none' } }}
              >
                {a.userDetail.deviceAccessHint}
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: deviceAccessOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                flexShrink: 0,
              }}
            />
          </Box>

          <Collapse in={deviceAccessOpen} timeout={220} unmountOnExit={false}>
            <Box
              sx={{
                mt: 1.25,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              {[
                { key: 'pro' as const, label: 'M-Photo Pro', shortLabel: 'Pro', checked: deviceAccess.pro },
                { key: 'lite' as const, label: 'M-Photo Lite', shortLabel: 'Lite', checked: deviceAccess.lite },
                { key: 'mono' as const, label: 'M-Photo Mono', shortLabel: 'Mono', checked: deviceAccess.mono },
              ].map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'space-between' },
                    gap: { xs: 0.15, sm: 1 },
                    px: { xs: 0.5, sm: 1.25 },
                    py: { xs: 0.65, sm: 0.75 },
                    minWidth: 0,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: item.checked ? alpha(theme.palette.primary.main, 0.35) : 'divider',
                    bgcolor: item.checked ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: '0.78rem', sm: '0.875rem' },
                      lineHeight: 1.2,
                      textAlign: 'center',
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      {item.shortLabel}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {item.label}
                    </Box>
                  </Typography>
                  <Switch
                    size="small"
                    checked={item.checked}
                    onChange={(e) => handleUpdateDeviceAccess(item.key, e.target.checked)}
                    disabled={updatingDeviceAccess}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      m: 0,
                      '& .MuiSwitch-switchBase': { p: 0.75 },
                    }}
                  />
                </Box>
              ))}
            </Box>
            {updatingDeviceAccess && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {t.common.saving}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
                Thời gian đăng nhập
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Tối thiểu 1 ngày hoặc vô hạn. Máy đang online nhận realtime qua socket.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: { xs: 0.75, sm: 1 },
                }}
              >
                {(
                  [
                    { key: 'pro' as const, label: 'Pro' },
                    { key: 'lite' as const, label: 'Lite' },
                    { key: 'mono' as const, label: 'Mono' },
                  ] as const
                ).map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                    }}
                  >
                    <Typography variant="body2" fontWeight={800}>
                      {item.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption">Vô hạn</Typography>
                      <Switch
                        size="small"
                        checked={sessionUnlimited[item.key]}
                        onChange={(e) =>
                          setSessionUnlimited((prev) => ({ ...prev, [item.key]: e.target.checked }))
                        }
                      />
                    </Box>
                    {!sessionUnlimited[item.key] && (
                      <TextField
                        size="small"
                        type="number"
                        label="Ngày"
                        inputProps={{ min: 1 }}
                        value={sessionDraftDays[item.key]}
                        onChange={(e) =>
                          setSessionDraftDays((prev) => ({ ...prev, [item.key]: e.target.value }))
                        }
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={updatingSessionChannel === item.key}
                      onClick={() => void handleSaveSessionPolicy(item.key)}
                    >
                      {updatingSessionChannel === item.key ? 'Đang lưu…' : 'Lưu'}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disabled={forcingLogoutChannel === item.key}
                      onClick={() => void handleForceLogoutChannel(item.key)}
                    >
                      {forcingLogoutChannel === item.key ? 'Đang gửi…' : 'Đăng xuất tất cả'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Hiện tại:{' '}
                      {loginDurationDays[item.key] == null
                        ? 'Vô hạn'
                        : `${loginDurationDays[item.key]} ngày`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
                Thời gian hết hạn collection
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                2 tuần–5 tháng (14–150 ngày), mặc định 90 ngày. Gallery lẻ = collection + 15 ngày.
                Máy online nhận realtime qua socket.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: { xs: 0.75, sm: 1 },
                }}
              >
                {(
                  [
                    { key: 'pro' as const, label: 'Pro' },
                    { key: 'lite' as const, label: 'Lite' },
                    { key: 'mono' as const, label: 'Mono' },
                  ] as const
                ).map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                    }}
                  >
                    <Typography variant="body2" fontWeight={800}>
                      {item.label}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="Ngày"
                      inputProps={{
                        min: api.COLLECTION_RETENTION_MIN_DAYS,
                        max: api.COLLECTION_RETENTION_MAX_DAYS,
                      }}
                      value={collectionDraftDays[item.key]}
                      onChange={(e) =>
                        setCollectionDraftDays((prev) => ({ ...prev, [item.key]: e.target.value }))
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={updatingCollectionChannel === item.key}
                      onClick={() => void handleSaveCollectionRetention(item.key)}
                    >
                      {updatingCollectionChannel === item.key ? 'Đang lưu…' : 'Lưu'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Hiện tại: {collectionRetentionDays[item.key]} ngày
                      <br />
                      Gallery lẻ: {collectionRetentionDays[item.key] + 15} ngày
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
                {a.userDetail.uploadMethod}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {a.userDetail.uploadMethodHint}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {(
                  [
                    {
                      key: 'server' as const,
                      title: a.userDetail.uploadMethodServer,
                      desc: a.userDetail.uploadMethodServerDesc,
                    },
                    {
                      key: 'signedUrl' as const,
                      title: a.userDetail.uploadMethodSigned,
                      desc: a.userDetail.uploadMethodSignedDesc,
                    },
                  ] as const
                ).map((opt) => {
                  const selected = galleryUploadMethod === opt.key;
                  return (
                    <Box
                      key={opt.key}
                      component="button"
                      type="button"
                      disabled={updatingUploadMethod}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleUpdateGalleryUploadMethod(opt.key);
                      }}
                      sx={{
                        textAlign: 'left',
                        cursor: updatingUploadMethod ? 'wait' : 'pointer',
                        border: '1px solid',
                        borderColor: selected ? alpha(theme.palette.primary.main, 0.45) : 'divider',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                        borderRadius: 2.5,
                        px: 1.25,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.35,
                      }}
                    >
                      <Typography variant="body2" fontWeight={800}>
                        {opt.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {updatingUploadMethod && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    {t.common.saving}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Paper>

      {/* Mobile tabs: sticky dưới navbar khi scroll */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: (t) => t.zIndex.appBar - 1,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          p: 0.5,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.background.paper, 0.97),
          backdropFilter: 'blur(12px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          mb: 0,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          orientation="horizontal"
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          ScrollButtonComponent={(props) => {
            const { direction, disabled, ...other } = props;
            return (
              <IconButton
                {...other}
                disabled={disabled}
                size="small"
                aria-label={direction === 'left' ? a.userDetail.tabPrev : a.userDetail.tabNext}
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 52,
                  borderRadius: 1.5,
                  color: 'primary.main',
                  '&.Mui-disabled': { color: 'text.disabled', opacity: 0.35 },
                }}
              >
                {direction === 'left' ? (
                  <ChevronLeftIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            );
          }}
          sx={{
            minHeight: 46,
            minWidth: 0,
            width: '100%',
            '& .MuiTabs-scrollButtons': {
              display: 'inline-flex',
              flexShrink: 0,
            },
            '& .MuiTabs-scroller': {
              overflowX: 'auto !important',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-flexContainer': {
              alignItems: 'stretch',
              gap: 0.5,
            },
            '& .MuiTab-root': {
              minHeight: 40,
              minWidth: 56,
              maxWidth: 76,
              px: 0.75,
              py: 0.75,
              justifyContent: 'center',
              textAlign: 'center',
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: 700,
              borderRadius: 2,
              color: 'text.secondary',
              flex: '0 0 auto',
              flexDirection: 'column',
              gap: 0.15,
              '& .MuiTab-iconWrapper': {
                marginBottom: '0 !important',
                marginRight: '0 !important',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              },
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tabs Navigation + Content Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '220px minmax(0, 1fr)' },
          gap: { xs: 1.5, md: 2.5 },
          alignItems: 'start',
          // Pro/Lite/Mono: sát tab sticky — tránh khoảng trống + stickyTop lệch
          mt: {
            xs: ['m-photo-pro', 'm-photo-lite', 'm-photo-mono'].includes(currentTab) ? 0.5 : 1.25,
            md: 0,
          },
          mb: 2,
          minWidth: 0,
          width: '100%',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'block' },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            position: 'sticky',
            top: 76,
            zIndex: 8,
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            orientation="vertical"
            variant="scrollable"
            scrollButtons={false}
            sx={{
              maxHeight: 'calc(100vh - 180px)',
              minWidth: 0,
              width: '100%',
              '& .MuiTabs-indicator': {
                display: 'block',
              },
              '& .MuiTabs-flexContainer': {
                alignItems: 'stretch',
                gap: 0.25,
              },
              '& .MuiTab-root': {
                minHeight: 44,
                minWidth: 0,
                px: 1.5,
                py: 1,
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                fontSize: '0.85rem',
                fontWeight: 700,
                borderRadius: 2,
                color: 'text.secondary',
                flex: '1 1 auto',
                flexDirection: 'row',
                gap: 0.75,
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Paper>
      {/* Không dùng overflowX:hidden — sẽ phá position:sticky của tab Pro/Lite/Mono */}
      <Box sx={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'visible' }}>

      {/* Tab Content */}
      <TabPanelEnter active={currentTab === 'profile'}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.75, md: 3 },
            borderRadius: 3.5,
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.16),
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            background: (t) =>
              `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${alpha('#fff', 0.98)} 42%, #fff 100%)`,
            boxShadow: (t) => `0 10px 28px ${alpha(t.palette.primary.main, 0.08)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  color: 'primary.main',
                }}
              >
                <LockOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>
                  {a.userDetail.changePassword}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.userDetail.changePasswordHint}
                </Typography>
              </Box>
            </Stack>

            <TextField
              label={a.userDetail.newPassword}
              type="password"
              fullWidth
              size="small"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              name="admin-change-user-new-password"
              autoComplete="new-password"
              inputProps={{
                'data-lpignore': 'true',
                autoComplete: 'new-password',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleOpenPasswordDialog();
                }
              }}
              sx={{ ...profileFieldSx, mb: 1.5 }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleOpenPasswordDialog}
              disabled={updatingPassword}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                py: 1.05,
                boxShadow: (t) => `0 10px 22px ${alpha(t.palette.primary.main, 0.28)}`,
              }}
            >
              {updatingPassword ? t.common.updating : a.userDetail.changePassword}
            </Button>

          <Dialog
            open={passwordDialogOpen}
            onClose={handleClosePasswordDialog}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                borderRadius: 3.5,
                overflow: 'hidden',
              },
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                color: 'common.white',
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${t.palette.secondary.main} 100%)`,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {a.userDetail.confirmAsAdmin}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Nhập mật khẩu admin để xác nhận
              </Typography>
            </Box>
            <DialogContent sx={{ pt: 2.5 }}>
              <TextField
                label={a.userDetail.adminPassword}
                type="password"
                fullWidth
                size="small"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                autoFocus
                name="admin-confirm-password"
                autoComplete="off"
                inputProps={{
                  'data-lpignore': 'true',
                  autoComplete: 'new-password',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmChangePassword();
                  }
                }}
                sx={profileFieldSx}
              />
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
              <Button
                onClick={handleClosePasswordDialog}
                disabled={updatingPassword}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
              >
                {t.common.cancel}
              </Button>
              <Button
                onClick={handleConfirmChangePassword}
                variant="contained"
                color="primary"
                disabled={updatingPassword}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, px: 2.25 }}
              >
                {updatingPassword ? t.common.updating : a.userDetail.confirm}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </TabPanelEnter>

      {userId && mountedProductTabs.has('m-photo-pro') && (
        <TabPanelEnter active={currentTab === 'm-photo-pro'} keepMounted>
          <AdminMPhotoPro userId={userId} />
        </TabPanelEnter>
      )}
      <TabPanelEnter active={(currentTab === 'videos' || currentTab === 'media') && Boolean(userId)}>
        {userId && <AdminVideos userId={userId} />}
      </TabPanelEnter>
      {userId && mountedProductTabs.has('m-photo-mono') && (
        <TabPanelEnter active={currentTab === 'm-photo-mono'} keepMounted>
          <AdminMPhotoMono userId={userId} />
        </TabPanelEnter>
      )}
      {userId && mountedProductTabs.has('m-photo-lite') && (
        <TabPanelEnter active={currentTab === 'm-photo-lite'} keepMounted>
          <AdminMPhotoLite userId={userId} />
        </TabPanelEnter>
      )}
      <TabPanelEnter active={currentTab === 'sepay' && Boolean(userId)}>
        {userId && <UserSePay userId={userId} />}
      </TabPanelEnter>
      <TabPanelEnter active={currentTab === 'staff' && Boolean(userId)}>
        {userId && <UserStaffManager ownerUserId={userId} />}
      </TabPanelEnter>

      <TabPanelEnter active={currentTab === 'mobile-upload'}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 1.25, fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.25rem' } }}
          >
            {a.userDetail.deviceSettings}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 0.75, sm: 1.25 },
              mb: 1.5,
            }}
          >
            {[
              {
                checked: mobileUploadEnabled,
                onChange: handleToggleMobileUpload,
                disabled: updatingMobileUpload,
                label: a.userDetail.toggleDevice,
                fullLabel: a.userDetail.toggleDeviceFull,
                desc: a.userDetail.toggleDeviceDesc,
              },
              {
                checked: editPageEnabled,
                onChange: handleToggleEditPage,
                disabled: updatingEditPage,
                label: a.userDetail.toggleEdit,
                fullLabel: a.userDetail.toggleEditFull,
                desc: a.userDetail.toggleEditDesc,
              },
              {
                checked: multiCaptureEnabled,
                onChange: handleToggleMultiCapture,
                disabled: updatingMultiCapture,
                label: a.userDetail.toggleMerge,
                fullLabel: a.userDetail.toggleMergeFull,
                desc: a.userDetail.toggleMergeDesc,
              },
            ].map((item) => (
              <Box
                key={item.fullLabel}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  px: { xs: 0.5, sm: 1.25 },
                  py: { xs: 0.85, sm: 1.25 },
                  minWidth: 0,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: item.checked ? alpha(theme.palette.primary.main, 0.35) : 'divider',
                  bgcolor: item.checked ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}
                >
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {item.label}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {item.fullLabel}
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.3, mb: 0.25 }}
                >
                  {item.desc}
                </Typography>
                <Switch
                  size="small"
                  checked={item.checked}
                  onChange={item.onChange}
                  disabled={item.disabled}
                  color="primary"
                  sx={{ m: 0 }}
                />
              </Box>
            ))}
          </Box>

          {userId && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PaletteIcon color="primary" fontSize="small" />
                  <Typography fontWeight={700} sx={{ fontSize: { xs: '0.85rem', md: '1rem' } }}>
                    {t.colorApps.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <AppAccentColorApps userId={userId} />
              </AccordionDetails>
            </Accordion>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
              {a.userDetail.machinesTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessage(a.userDetail.machinesCount, { n: machines.length })}
            </Typography>
          </Stack>

          {!mobileUploadEnabled && (
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
              {a.userDetail.deviceOffWarning}
            </Alert>
          )}

          {/* Mobile: chọn máy dạng cuộn ngang */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              gap: 1,
              overflowX: 'auto',
              pb: 1,
              mb: 1.5,
              mx: -0.5,
              px: 0.5,
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { height: 4 },
            }}
          >
            {machinesLoading ? (
              <Box py={2} px={2}>
                <CircularProgress size={22} />
              </Box>
            ) : machines.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1.5 }}>
                      {a.userDetail.noMachines}
              </Typography>
            ) : (
              machines.map((machine, index) => {
                const selected = selectedMachineCode === machine.machineCode;
                const running = Boolean(machine.isAppRunning);
                const productLabel = machine.appProduct ? String(machine.appProduct).toUpperCase() : null;
                const productColor = appProductLabelColor(machine.appProduct);
                return (
                  <Box
                    key={machine.machineCode}
                    onClick={() => handleSelectMachine(machine.machineCode)}
                    sx={{
                      flex: '0 0 auto',
                      minWidth: 132,
                      maxWidth: 160,
                      p: 1.1,
                      borderRadius: 2.5,
                      border: '1.5px solid',
                      borderColor: selected
                        ? 'primary.main'
                        : running
                          ? alpha('#16a34a', 0.45)
                          : 'divider',
                      bgcolor: running ? alpha('#16a34a', 0.14) : 'background.paper',
                      cursor: 'pointer',
                      boxShadow: selected ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                      ...userCardEnterSx(index),
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ fontSize: '0.8rem' }}>
                        {machine.machineName || machine.machineCode}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <Chip
                          label={running ? a.userDetail.on : a.userDetail.off}
                          color={running ? 'success' : 'default'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          disabled={forcingLogoutMachine === machine.machineCode}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleForceLogoutMachine(
                              machine.machineCode,
                              machine.machineName || machine.machineCode
                            );
                          }}
                          sx={{ p: 0.35 }}
                        >
                          <LogoutIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.35 }}>
                      {machine.machineCode}
                      {productLabel ? ' · ' : ''}
                      {productLabel && (
                        <Box component="span" sx={{ color: productColor, fontWeight: 800 }}>
                          {productLabel}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>

          <Grid container spacing={2} alignItems="stretch">
            {/* Desktop machine list */}
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  ...(rightMachinePanelHeight > 0
                    ? { height: `${rightMachinePanelHeight}px` }
                    : {}),
                }}
              >
                {machinesLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, minHeight: 180 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : machines.length === 0 ? (
                  <Box p={2} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {a.userDetail.noMachines}
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
                    {machines.map((machine) => {
                      const selected = selectedMachineCode === machine.machineCode;
                      const running = Boolean(machine.isAppRunning);
                      const productLabel = machine.appProduct ? String(machine.appProduct).toUpperCase() : null;
                      const productColor = appProductLabelColor(machine.appProduct);
                      return (
                        <ListItemButton
                          key={machine.machineCode}
                          selected={selected}
                          onClick={() => handleSelectMachine(machine.machineCode)}
                          sx={{
                            py: 1.35,
                            px: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: running ? alpha('#16a34a', 0.12) : 'transparent',
                            '&.Mui-selected': {
                              bgcolor: running
                                ? alpha('#16a34a', 0.18)
                                : (t) => alpha(t.palette.primary.main, 0.08),
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: running
                                ? alpha('#16a34a', 0.22)
                                : (t) => alpha(t.palette.primary.main, 0.12),
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight={800} noWrap>
                                {machine.machineName || machine.machineCode}
                              </Typography>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '7.6rem 4.6rem 2.8rem',
                                  columnGap: 0.75,
                                  alignItems: 'center',
                                  mt: 0.15,
                                  width: 'fit-content',
                                  maxWidth: '100%',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  component="span"
                                  sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                                >
                                  {`Code: ${machine.machineCode}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap component="span">
                                  {machine.appPlatform === 'android'
                                    ? 'Android'
                                    : machine.appPlatform === 'windows'
                                      ? 'Windows'
                                      : '—'}
                                </Typography>
                                {productLabel ? (
                                  <Typography
                                    variant="caption"
                                    component="span"
                                    noWrap
                                    sx={{ color: productColor, fontWeight: 800, letterSpacing: 0.02 }}
                                  >
                                    {productLabel}
                                  </Typography>
                                ) : (
                                  <Box component="span" />
                                )}
                              </Box>
                            }
                          />
                          <Chip
                            label={running ? t.deviceMgmt.running : t.deviceMgmt.paused}
                            color={running ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 700, height: 24, ml: 1 }}
                          />
                          <Tooltip title="Đăng xuất máy này">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={forcingLogoutMachine === machine.machineCode}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleForceLogoutMachine(
                                    machine.machineCode,
                                    machine.machineName || machine.machineCode
                                  );
                                }}
                                sx={{ ml: 0.5 }}
                              >
                                {forcingLogoutMachine === machine.machineCode ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <LogoutIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
              <Paper
                ref={rightMachinePanelRef}
                elevation={0}
                sx={{
                  p: { xs: 1.25, md: 2 },
                  flex: 1,
                  width: '100%',
                  maxWidth: '100%',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                {machineDetailLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={180}>
                    <CircularProgress />
                  </Box>
                ) : !machineDetail ? (
                  <Typography variant="body2" color="text.secondary">
                    Chọn một máy phía trên để xem mã / QR.
                  </Typography>
                ) : (
                  <>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                      <Computer color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: { xs: '0.95rem', md: '1.2rem' } }}>
                        {machineDetail.machineName || machineDetail.machineCode}
                      </Typography>
                      <MachineRenameControl
                        machineCode={machineDetail.machineCode}
                        machineName={machineDetail.machineName}
                        onRenamed={(machineName) => {
                          setMachineDetail((prev) => (prev ? { ...prev, machineName } : prev));
                          setMachines((prev) =>
                            prev.map((m) =>
                              m.machineCode === machineDetail.machineCode
                                ? { ...m, machineName }
                                : m
                            )
                          );
                        }}
                      />
                      <Chip
                        label={machineDetail.isAppRunning ? t.deviceMgmt.running : t.deviceMgmt.paused}
                        color={machineDetail.isAppRunning ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<LogoutIcon fontSize="small" />}
                        disabled={forcingLogoutMachine === machineDetail.machineCode}
                        onClick={() =>
                          void handleForceLogoutMachine(
                            machineDetail.machineCode,
                            machineDetail.machineName || machineDetail.machineCode
                          )
                        }
                        sx={{ ml: { xs: 0, sm: 'auto' }, fontWeight: 800 }}
                      >
                        {forcingLogoutMachine === machineDetail.machineCode
                          ? 'Đang gửi…'
                          : 'Đăng xuất máy này'}
                      </Button>
                    </Box>

                    {(() => {
                      const uploadPageUrl  = `${currentAppOrigin()}/mobile-upload/machine/${machineDetail.fullCode}`;
                      const backendOrigin  = API_URL.replace(/\/api\/?$/i, '');
                      const controlPageUrl = `${backendOrigin}/controlpage/${machineDetail.machineCode}`;
                      const editPageUrl = `${backendOrigin}/editpage/${machineDetail.machineCode}`;
                      return (
                        <MachineDeviceMgmtTabs
                          userId={userId!}
                          machineDetail={machineDetail}
                          uploadPageUrl={uploadPageUrl}
                          controlPageUrl={controlPageUrl}
                          editPageUrl={editPageUrl}
                          copiedTarget={copiedTarget}
                          onCopy={(text, target) => handleCopyText(text, target as 'random' | 'link')}
                          formatDateTime={formatDateTime}
                          manageControlPageUi
                        />
                      );
                    })()}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </TabPanelEnter>
      </Box>
      </Box>

      <Dialog
        open={Boolean(logoutConfirm)}
        onClose={closeLogoutConfirm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3.5, overflow: 'hidden' },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            color: 'common.white',
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.error.dark} 0%, ${t.palette.error.main} 55%, ${t.palette.error.light} 100%)`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Xác nhận đăng xuất
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <DialogContentText sx={{ color: 'text.primary', fontWeight: 600 }}>
            {logoutConfirm?.type === 'channel'
              ? `Đăng xuất TẤT CẢ máy ${logoutConfirm.channel.toUpperCase()} của user này?`
              : logoutConfirm?.type === 'machine'
                ? `Đăng xuất máy "${logoutConfirm.label}" (${logoutConfirm.machineCode})?`
                : ''}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontSize: 13 }}>
            Máy sẽ bị đăng xuất ngay khi nhận được lệnh.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={closeLogoutConfirm}
            disabled={Boolean(forcingLogoutChannel || forcingLogoutMachine)}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={() => void confirmForceLogout()}
            variant="contained"
            color="error"
            disabled={Boolean(forcingLogoutChannel || forcingLogoutMachine)}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, px: 2.25 }}
          >
            {forcingLogoutChannel || forcingLogoutMachine ? 'Đang gửi…' : 'Đăng xuất'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetail;
